import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { debounceTime } from "rxjs/operators";
import { asEnumerable } from "linq-es2015";
import { GeNameValidator } from "./validators/ge-name.validator";
import { ResolvedRequestOptionsService } from "../../services/resolved-request-options";
import { ExperimentDesignFormValidatorsService } from "../experiment-design-form/experiment-design-form-validators.service";
import { CropService } from "../../../shared/services/crop.service";
import { PollinationTypeEnum } from "../../../shared/services/pollination-stake-label.service";
import { PollinationCategoryEnum } from "../../../shared/services/pollination-category.service";
import { ConjunctionEnum } from "../../../shared/models/conjunction.enum";
var PollinationInstructionsComponent = /** @class */ (function () {
    function PollinationInstructionsComponent(formBuilder, geNameValidator, formValidators, cropService, routeData) {
        this.formBuilder = formBuilder;
        this.geNameValidator = geNameValidator;
        this.formValidators = formValidators;
        this.cropService = cropService;
        this.routeData = routeData;
        this.focusedPreference = -1;
        this.showDonorNeeded = false;
        this.tooltipMessage = "";
        this.pollinationCategoryTooltipText = "";
        this.initialTooltipMessage = "This toggle notifies your Tactical Liaison to submit a corresponding MyCE request to plant pollen or silk donors";
        this.crossPollinationTooltipMessage = "When selecting a cross pollination option (carry pollen out or carry pollen in), donors will likely be needed";
        this.showPollinationInstructions = true;
        this.canAddPreferenceGroup = false;
        this.canDeletePreferenceGroup = false;
        this.ceRequestOptions = this.routeData.requestOptions;
    }
    Object.defineProperty(PollinationInstructionsComponent.prototype, "pollinationInstructions", {
        get: function () {
            return this.experimentDesignFormGroup.get("pollinationInstructions");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PollinationInstructionsComponent.prototype, "pollinationPreferences", {
        get: function () {
            return this.experimentDesignFormGroup.get("pollinationInstructions.pollinationPreferences");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PollinationInstructionsComponent.prototype, "pollinationCategory", {
        get: function () {
            return this.pollinationInstructions.get("pollinationCategory");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PollinationInstructionsComponent.prototype, "donorsNeeded", {
        get: function () {
            return this.pollinationInstructions.get('donorsNeeded');
        },
        enumerable: true,
        configurable: true
    });
    PollinationInstructionsComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.cropService.get(this.crop).subscribe(function (x) {
            _this.showDonorNeeded = x.isDonorNeeded;
        });
        this.pollinationCategoryOptions = asEnumerable(this.ceRequestOptions.pollinationCategories)
            .Where(function (s) { return s.isActive; })
            .OrderBy(function (s) { return s.order; })
            .ToArray();
        this.tooltipMessage = this.initialTooltipMessage;
        this.pollinationCategoryTooltipText = 'This field specifies what category of pollinations will be performed, and the options available for those categories.<br><br>' +
            'Standard: This covers most experiments. Two pollination options are allowed & required - "None" should be used as the secondary option if back-up instruction is not desired.<br><br>' +
            'Zygosity Dependent: Allows pollination instructions to be specified based on zygosity. Each zygosity follows the same rules as the "Standard" category.<br><br>' +
            'Crossing block: Use for crossing block experiments only for situations approved by your pipeline and TL.';
        this.setupPollinationCategoryListener();
        var currentCategoryValue = this.pollinationCategory ? this.pollinationCategory.value : null;
        this.updatePollinationInstructionsState(currentCategoryValue);
    };
    PollinationInstructionsComponent.prototype.ngOnDestroy = function () {
        if (this.pollinationCategorySubscription) {
            this.pollinationCategorySubscription.unsubscribe();
        }
    };
    PollinationInstructionsComponent.prototype.setupPollinationCategoryListener = function () {
        var _this = this;
        if (this.pollinationCategory && this.pollinationCategory.valueChanges) {
            this.pollinationCategorySubscription = this.pollinationCategory.valueChanges
                .pipe(debounceTime(50))
                .subscribe(function (categoryId) {
                _this.updatePollinationInstructionsState(categoryId);
            });
        }
    };
    PollinationInstructionsComponent.prototype.updatePollinationInstructionsState = function (categoryId) {
        var selectedCategory = this.pollinationCategoryOptions.find(function (option) { return option.id === categoryId; });
        if (!categoryId || !selectedCategory) {
            this.showPollinationInstructions = false;
            this.canAddPreferenceGroup = false;
            this.canDeletePreferenceGroup = false;
            this.adjustPollinationPreferenceGroupCount(0);
            this.pollinationPreferences.clearValidators();
            this.pollinationPreferences.updateValueAndValidity();
            if (this.showDonorNeeded) {
                this.pollinationInstructions.get('donorsNeeded').setValue(false);
                this.tooltipMessage = this.initialTooltipMessage;
            }
            return;
        }
        var numberOfGroups = selectedCategory.numberOfPreferenceGroups;
        this.showPollinationInstructions = selectedCategory.id !== PollinationCategoryEnum.CrossingBlock;
        this.canAddPreferenceGroup = false;
        this.canDeletePreferenceGroup = false;
        if (selectedCategory.id === PollinationCategoryEnum.CrossingBlock) {
            this.pollinationPreferences.clearValidators();
        }
        else {
            this.pollinationPreferences.setValidators(Validators.required);
        }
        this.adjustPollinationPreferenceGroupCount(numberOfGroups);
        if (numberOfGroups > 0) {
            this.pollinationInstructions.updateValueAndValidity();
        }
        this.pollinationPreferences.updateValueAndValidity();
        this.checkAndResetDonorsNeeded();
    };
    PollinationInstructionsComponent.prototype.adjustPollinationPreferenceGroupCount = function (targetCount) {
        var _this = this;
        var currentCount = this.pollinationPreferences.length;
        if (currentCount === targetCount) {
            if (targetCount > 0 && this.focusedPreference === -1) {
                this.focusedPreference = 0;
            }
            return;
        }
        while (this.pollinationPreferences.length > 0) {
            this.pollinationPreferences.removeAt(0);
        }
        for (var i = 0; i < targetCount; i++) {
            this.addPreferenceGroup();
        }
        if (targetCount > 0) {
            this.focusedPreference = 0;
            setTimeout(function () {
                _this.pollinationPreferences.controls.forEach(function (control) {
                    control.markAsTouched();
                    control.updateValueAndValidity();
                });
            }, 0);
        }
        else {
            this.focusedPreference = -1;
        }
    };
    PollinationInstructionsComponent.prototype.canAddPollinationInstruction = function (preferenceGroupIndex, pollinationTypeId) {
        var _this = this;
        if (!this.pollinationCategory.value) {
            return false;
        }
        var selectedCategory = this.pollinationCategoryOptions.find(function (option) { return option.id === _this.pollinationCategory.value; });
        if (!selectedCategory) {
            return false;
        }
        var instructionsPerPair = selectedCategory.numberOfInstructionsPerPair;
        if (selectedCategory.id === PollinationCategoryEnum.CrossingBlock) {
            return false;
        }
        var nonePollinationType = this.ceRequestOptions.pollinationTypes.find(function (pt) { return pt.id === PollinationTypeEnum.None; });
        var isAddingNone = pollinationTypeId && nonePollinationType && pollinationTypeId === nonePollinationType.id;
        if (this.groupHasNone(preferenceGroupIndex)) {
            return false;
        }
        if (isAddingNone) {
            var currentGroupNonNoneInstructions = this.getNonNoneInstructionsInGroup(preferenceGroupIndex);
            if (currentGroupNonNoneInstructions > 0) {
                return false;
            }
            return true;
        }
        if (selectedCategory.id === PollinationCategoryEnum.Standard) {
            var totalNonNoneInstructions = this.getTotalNonNoneInstructions();
            var currentGroupNonNoneInstructions = this.getNonNoneInstructionsInGroup(preferenceGroupIndex);
            return totalNonNoneInstructions < instructionsPerPair && currentGroupNonNoneInstructions < instructionsPerPair;
        }
        if (selectedCategory.id === PollinationCategoryEnum.ZygosityDependent) {
            var pairIndex = Math.floor(preferenceGroupIndex / 2);
            var pairStartIndex = pairIndex * 2;
            var pairEndIndex = pairStartIndex + 1;
            var pairTotalInstructions = this.getNonNoneInstructionsInGroup(pairStartIndex) +
                this.getNonNoneInstructionsInGroup(pairEndIndex);
            var currentGroupZygosityInstructions = this.getNonNoneInstructionsInGroup(preferenceGroupIndex);
            return pairTotalInstructions < instructionsPerPair && currentGroupZygosityInstructions < instructionsPerPair;
        }
        return false;
    };
    PollinationInstructionsComponent.prototype.groupHasNone = function (groupIndex) {
        var nonePollinationType = this.ceRequestOptions.pollinationTypes.find(function (pt) { return pt.id === PollinationTypeEnum.None; });
        if (!nonePollinationType)
            return false;
        var group = this.pollinationPreferences.at(groupIndex);
        if (!group)
            return false;
        var items = group.get('items');
        if (!items)
            return false;
        for (var i = 0; i < items.length; i++) {
            var item = items.at(i);
            var pollinationTypeControl = item.get('pollinationType');
            if (pollinationTypeControl && pollinationTypeControl.value === nonePollinationType.id) {
                return true;
            }
        }
        return false;
    };
    PollinationInstructionsComponent.prototype.getTotalNonNoneInstructions = function () {
        var total = 0;
        for (var i = 0; i < this.pollinationPreferences.length; i++) {
            total += this.getNonNoneInstructionsInGroup(i);
        }
        return total;
    };
    PollinationInstructionsComponent.prototype.getNonNoneInstructionsInGroup = function (groupIndex) {
        var group = this.pollinationPreferences.at(groupIndex);
        if (!group)
            return 0;
        var items = group.get('items');
        if (!items)
            return 0;
        var count = 0;
        var _loop_1 = function (i) {
            var item = items.at(i);
            var pollinationTypeControl = item.get('pollinationType');
            var pollinationType = pollinationTypeControl ? pollinationTypeControl.value : null;
            if (pollinationType !== null && pollinationType !== undefined) {
                var pollinationTypeObj = this_1.ceRequestOptions.pollinationTypes.find(function (pt) { return pt.id === pollinationType; });
                if (pollinationTypeObj && pollinationTypeObj.id !== PollinationTypeEnum.None) {
                    count++;
                }
            }
        };
        var this_1 = this;
        for (var i = 0; i < items.length; i++) {
            _loop_1(i);
        }
        return count;
    };
    PollinationInstructionsComponent.prototype.addPreferenceGroup = function () {
        var newPreference = this.formBuilder.group({
            order: this.pollinationPreferences.length + 1,
            conjunction: this.formBuilder.control({ value: ConjunctionEnum.And, disabled: false }, []),
            items: this.formBuilder.array([]),
        }, { validator: this.formValidators.pollinationPreferenceGroupValidator() });
        this.pollinationPreferences.push(newPreference);
        this.focusedPreference = this.pollinationPreferences.length - 1;
    };
    PollinationInstructionsComponent.prototype.addInstruction = function (pollinationTypeId) {
        if (this.pollinationPreferences.length === 0) {
            return;
        }
        if (!this.canAddPollinationInstruction(this.focusedPreference, pollinationTypeId)) {
            return;
        }
        var pollinationTypes = asEnumerable(this.ceRequestOptions.pollinationTypes);
        var selectedPollinationType = pollinationTypes.Single(function (t) { return t.id === pollinationTypeId; });
        if (selectedPollinationType.id === PollinationTypeEnum.None) {
            var currentlySelectedPreference_1 = this.pollinationPreferences.at(this.focusedPreference);
            var items_1 = currentlySelectedPreference_1.get("items");
            while (items_1.length > 0) {
                items_1.removeAt(0);
            }
        }
        var geName = this.formBuilder.control({ value: null, disabled: false }, []);
        if (selectedPollinationType.geNameRequired) {
            geName.setValidators([Validators.required, Validators.minLength(3)]);
            geName.setAsyncValidators(this.geNameValidator.validateGeName());
            if (this.showDonorNeeded) {
                this.pollinationInstructions.get('donorsNeeded').setValue(true);
                this.tooltipMessage = this.crossPollinationTooltipMessage;
            }
        }
        geName.patchValue(null);
        geName.updateValueAndValidity();
        var currentlySelectedPreference = this.pollinationPreferences.at(this.focusedPreference);
        var items = currentlySelectedPreference.get("items");
        items.push(this.formBuilder.group({
            conjunction: this.formBuilder.control({ value: ConjunctionEnum.Or, disabled: false }, []),
            pollinationType: this.formBuilder.control({ value: pollinationTypeId, disabled: false }, []),
            geName: geName
        }));
        this.handleAutomaticNoneForStandard();
    };
    PollinationInstructionsComponent.prototype.deleteLastItemFromGroup = function (groupIndex) {
        var group = this.pollinationPreferences.at(groupIndex);
        if (!group)
            return;
        var items = group.get('items');
        if (!items || items.length === 0)
            return;
        items.removeAt(items.length - 1);
        this.handleAutomaticNoneForStandard();
        this.pollinationInstructions.updateValueAndValidity();
        this.checkAndResetDonorsNeeded();
    };
    PollinationInstructionsComponent.prototype.handleAutomaticNoneForStandard = function () {
        var _this = this;
        var selectedCategory = this.pollinationCategoryOptions.find(function (option) { return option.id === _this.pollinationCategory.value; });
        if (!selectedCategory) {
            return;
        }
        var numberOfGroups = selectedCategory.numberOfPreferenceGroups;
        if (selectedCategory.id === PollinationCategoryEnum.Standard && numberOfGroups === 2) {
            this.handleNoneForGroupPair(0, 1, selectedCategory.numberOfInstructionsPerPair);
        }
        else if (selectedCategory.id === PollinationCategoryEnum.ZygosityDependent && numberOfGroups === 6) {
            this.handleNoneForGroupPair(0, 1, selectedCategory.numberOfInstructionsPerPair);
            this.handleNoneForGroupPair(2, 3, selectedCategory.numberOfInstructionsPerPair);
            this.handleNoneForGroupPair(4, 5, selectedCategory.numberOfInstructionsPerPair);
        }
    };
    PollinationInstructionsComponent.prototype.handleNoneForGroupPair = function (group1Index, group2Index, instructionsPerPair) {
        var group1Instructions = this.getNonNoneInstructionsInGroup(group1Index);
        var group2Instructions = this.getNonNoneInstructionsInGroup(group2Index);
        if (group1Instructions === instructionsPerPair && group2Instructions === 0) {
            this.clearGroupAndAddNone(group2Index);
        }
        else if (group2Instructions === instructionsPerPair && group1Instructions === 0) {
            this.clearGroupAndAddNone(group1Index);
        }
    };
    PollinationInstructionsComponent.prototype.clearGroupAndAddNone = function (groupIndex) {
        var nonePollinationType = this.ceRequestOptions.pollinationTypes.find(function (pt) { return pt.id === PollinationTypeEnum.None; });
        if (!nonePollinationType)
            return;
        var group = this.pollinationPreferences.at(groupIndex);
        if (!group)
            return;
        var items = group.get('items');
        if (!items)
            return;
        while (items.length > 0) {
            items.removeAt(0);
        }
        var geName = this.formBuilder.control({ value: null, disabled: false }, []);
        items.push(this.formBuilder.group({
            conjunction: this.formBuilder.control({ value: ConjunctionEnum.Or, disabled: false }, []),
            pollinationType: this.formBuilder.control({ value: nonePollinationType.id, disabled: false }, []),
            geName: geName
        }));
    };
    PollinationInstructionsComponent.prototype.removeNoneFromGroup = function (groupIndex) {
        var nonePollinationType = this.ceRequestOptions.pollinationTypes.find(function (pt) { return pt.id === PollinationTypeEnum.None; });
        if (!nonePollinationType)
            return;
        var group = this.pollinationPreferences.at(groupIndex);
        if (!group)
            return;
        var items = group.get('items');
        if (!items)
            return;
        for (var i = items.length - 1; i >= 0; i--) {
            var item = items.at(i);
            var pollinationTypeControl = item.get('pollinationType');
            if (pollinationTypeControl && pollinationTypeControl.value === nonePollinationType.id) {
                items.removeAt(i);
            }
        }
    };
    PollinationInstructionsComponent.prototype.dropped = function (event) {
        var pollinationPreferencesCopy = [];
        for (var _i = 0, _a = this.pollinationPreferences.controls; _i < _a.length; _i++) {
            var preference = _a[_i];
            pollinationPreferencesCopy.push(preference.value);
        }
        this.pollinationPreferences.reset(pollinationPreferencesCopy);
    };
    PollinationInstructionsComponent.prototype.delete = function (index) {
        if (!this.canDeletePreferenceGroup) {
            return;
        }
        this.pollinationPreferences.removeAt(index);
        this.checkAndResetDonorsNeeded();
        this.pollinationInstructions.updateValueAndValidity();
    };
    PollinationInstructionsComponent.prototype.checkAndResetDonorsNeeded = function () {
        if (!this.showDonorNeeded) {
            return;
        }
        var hasCrossPollination = this.hasCrossPollinationInstructions();
        if (!hasCrossPollination) {
            this.pollinationInstructions.get('donorsNeeded').setValue(false);
            this.tooltipMessage = this.initialTooltipMessage;
        }
    };
    PollinationInstructionsComponent.prototype.hasCrossPollinationInstructions = function () {
        for (var i = 0; i < this.pollinationPreferences.length; i++) {
            var group = this.pollinationPreferences.at(i);
            if (!group)
                continue;
            var items = group.get('items');
            if (!items)
                continue;
            for (var j = 0; j < items.length; j++) {
                var item = items.at(j);
                var pollinationTypeControl = item.get('pollinationType');
                if (pollinationTypeControl) {
                    var pollinationType = pollinationTypeControl.value;
                    if (pollinationType === PollinationTypeEnum.CarryTo ||
                        pollinationType === PollinationTypeEnum.CarryFrom) {
                        return true;
                    }
                }
            }
        }
        return false;
    };
    PollinationInstructionsComponent.prototype.focus = function (index) {
        this.focusedPreference = index;
    };
    PollinationInstructionsComponent.prototype.getGroupLabel = function (groupIndex) {
        var _this = this;
        if (!this.pollinationCategory.value) {
            return '';
        }
        var selectedCategory = this.pollinationCategoryOptions.find(function (option) { return option.id === _this.pollinationCategory.value; });
        if (!selectedCategory) {
            return '';
        }
        return PollinationInstructionsComponent.getGroupLabelByCategoryId(groupIndex, selectedCategory.id);
    };
    PollinationInstructionsComponent.getGroupLabelByCategoryId = function (groupIndex, categoryId) {
        switch (categoryId) {
            case PollinationCategoryEnum.Standard:
                if (groupIndex === 0)
                    return 'Primary Standard';
                if (groupIndex === 1)
                    return 'Secondary Standard';
                return '';
            case PollinationCategoryEnum.ZygosityDependent:
                if (groupIndex === 0)
                    return 'Primary Homozygous';
                if (groupIndex === 1)
                    return 'Secondary Homozygous';
                if (groupIndex === 2)
                    return 'Primary Hemizygous';
                if (groupIndex === 3)
                    return 'Secondary Hemizygous';
                if (groupIndex === 4)
                    return 'Primary Null/WT';
                if (groupIndex === 5)
                    return 'Secondary Null/WT';
                return '';
            default:
                return '';
        }
    };
    PollinationInstructionsComponent.getCategoryIdFromName = function (categoryName) {
        if (!categoryName) {
            return null;
        }
        switch (categoryName) {
            case 'Standard':
                return PollinationCategoryEnum.Standard;
            case 'Zygosity-dependent':
                return PollinationCategoryEnum.ZygosityDependent;
            case 'Crossing block':
                return PollinationCategoryEnum.CrossingBlock;
            default:
                return null;
        }
    };
    PollinationInstructionsComponent.getGroupLabelForCategory = function (groupIndex, categoryName) {
        var categoryId = PollinationInstructionsComponent.getCategoryIdFromName(categoryName);
        if (!categoryId) {
            return '';
        }
        return PollinationInstructionsComponent.getGroupLabelByCategoryId(groupIndex, categoryId);
    };
    PollinationInstructionsComponent.prototype.isZygosityDependent = function () {
        var _this = this;
        if (!this.pollinationCategory.value) {
            return false;
        }
        var selectedCategory = this.pollinationCategoryOptions.find(function (option) { return option.id === _this.pollinationCategory.value; });
        return selectedCategory ? selectedCategory.id === PollinationCategoryEnum.ZygosityDependent : false;
    };
    PollinationInstructionsComponent.prototype.shouldHideConjunction = function (groupIndex) {
        if (groupIndex === 0) {
            return false;
        }
        if (!this.pollinationCategory.value) {
            return false;
        }
        return true;
    };
    PollinationInstructionsComponent.shouldHideConjunctionForCategory = function (groupIndex, categoryId) {
        if (groupIndex === 0) {
            return false;
        }
        if (!categoryId) {
            return false;
        }
        return true;
    };
    PollinationInstructionsComponent.shouldHideConjunctionByCategoryName = function (groupIndex, categoryName) {
        if (groupIndex === 0) {
            return false;
        }
        var categoryId = PollinationInstructionsComponent.getCategoryIdFromName(categoryName);
        if (!categoryId) {
            return false;
        }
        return PollinationInstructionsComponent.shouldHideConjunctionForCategory(groupIndex, categoryId);
    };
    return PollinationInstructionsComponent;
}());
export { PollinationInstructionsComponent };



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/experiment-design/pollination-instructions/pollination-instructions.component.ts.pre-build-optimizer.js